/**
 * directory.v1 bulk operations, as bff-web ships them
 * (src/lead/directorycontract/schema.json on staging). Field names follow the
 * wire format on purpose: these types describe the contract, not a view model.
 */

export type tOperationAction =
  | { type: 'archive' }
  | { type: 'delete' }
  | {
      type: 'move';
      funnel_uuid: string;
      stage_uuid: string | null;
      in_inbox: boolean;
    }
  | { type: 'assign'; user_uuid: string | null }
  | { type: 'tag'; mode: 'assign' | 'remove'; tag_ids: number[] }
  | { type: 'tag'; mode: 'edit'; tag_ids: number[]; remove_tag_ids: number[] };

export type tOperationKind = tOperationAction['type'];

/** A captured selection resource (the server's snapshot of the picks). */
export type tCapturedSelection = {
  id: string;
  mode: 'explicit' | 'matching';
  revision: number;
  captured_at: string;
  expires_at: string;
  member_count: number;
  excluded_count: number;
  selected_count: number;
};

export type tSelectionRef = { id: string; revision: number };

/** A funnel the new owner of an assignment cannot see, and how many of the leads are in it. */
export type tUnseenFunnel = { uuid: string; name: string; affected_count: number };

/**
 * What a preview warns about before the action runs. At most one per preview:
 * automatic assignment belongs to a move, the assignee's access to an assignment.
 */
export type tPreviewWarning =
  /** How many unassigned leads the destination will auto-assign. */
  | { reason: 'automatic_assignment'; affected_count: number }
  /**
   * Contract revision 2 (BUG-1031): the leads are assigned anyway, to somebody
   * who cannot see the funnel they are in. `funnels` names at most ten, the
   * ones holding most of those leads first; `affected_count` counts them all.
   */
  | { reason: 'assignee_without_funnel_access'; affected_count: number; funnels: tUnseenFunnel[] };

export type tOperationPreview = {
  id: string;
  selection: tSelectionRef;
  action: tOperationAction;
  expires_at: string;
  selected_count: number;
  eligible_count: number;
  noop_count: number;
  blocked_count: number;
  warnings: tPreviewWarning[];
  /**
   * Contract revision 2: the blocked leads by reason. Missing in an answer of
   * revision 1 (leads-service or bff-web not there yet), where it means "not
   * said", never "none".
   */
  blocked_reasons?: Partial<Record<tOperationReason | string, number>>;
  confirmation_allowed: boolean;
};

export type tOperationState =
  | 'queued'
  | 'running'
  | 'completed'
  | 'partially_completed'
  | 'failed';

export type tOperationCounts = {
  changed: number;
  noop: number;
  skipped: number;
  failed: number;
  pending: number;
};

/** Why leads were skipped or failed, with how many each. */
export type tOperationReason =
  | 'permission_changed'
  | 'invalid_assignee'
  | 'state_changed'
  | 'policy_changed'
  | 'execution_failed'
  | 'source_missing'
  | 'tag_limit_exceeded';

export type tBulkOperation = {
  id: string;
  action: tOperationKind | 'undo';
  state: tOperationState;
  target_count: number;
  counts: tOperationCounts;
  reasons: Partial<Record<tOperationReason | string, number>>;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  /** The server's Undo deadline; null when this operation cannot be undone. */
  undo_expires_at: string | null;
  undo_of: string | null;
};

export type tOperationPage = {
  operations: tBulkOperation[];
  next_cursor: string | null;
};

export const TERMINAL_OPERATION_STATES: ReadonlySet<tOperationState> = new Set([
  'completed',
  'partially_completed',
  'failed',
]);

/** The closed set of directory.v1 error reasons, by HTTP status. */
export const DIRECTORY_ERROR_REASONS = [
  'actor_required',
  'membership_required',
  'resource_not_found',
  'selection_revision_changed',
  'preview_stale',
  'idempotency_conflict',
  'operation_conflict',
  'undo_unavailable',
  'selection_expired',
  'preview_expired',
  'undo_expired',
  'request_too_large',
  'invalid_query',
  'invalid_action',
  'invalid_destination',
  'invalid_assignee',
  'quota_exceeded',
  'unsupported_contract',
  'rate_limited',
  'capture_unavailable',
  'quota_unavailable',
  'policy_unavailable',
] as const;

export type tDirectoryErrorReason = (typeof DIRECTORY_ERROR_REASONS)[number];

export type tDirectoryApiError = {
  /** HTTP status; 0 when the request never got an answer. */
  status: number;
  /** A contract reason, `network` without an answer, `unknown` otherwise. */
  reason: tDirectoryErrorReason | 'network' | 'unknown';
  retryable: boolean;
  /** From `Retry-After` on a 429, in seconds. */
  retryAfterSeconds?: number;
  /** On `quota_exceeded`: what the action needs and what is free. */
  details?: { required_capacity?: number; available_capacity?: number };
};
